import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getArticleByCategoryAndId, getRelatedArticles } from "@/mocks/articleDetailData";
import { useState } from "react";

const categoryLabelMap: Record<string, { name: string; color: string; bg: string; text: string; border: string }> = {
  wanghong: { name: "网红翻车现场", color: "primary", bg: "bg-primary-500", text: "text-primary-500", border: "border-primary-300" },
  fancha: { name: "反差视频精选", color: "accent", bg: "bg-accent-500", text: "text-accent-500", border: "border-accent-300" },
  jiudian: { name: "酒店猛料曝光", color: "secondary", bg: "bg-secondary-500", text: "text-secondary-500", border: "border-secondary-300" },
};

export default function ArticleDetailPage() {
  const { category, articleId } = useParams<{ category: string; articleId: string }>();
  const article = getArticleByCategoryAndId(category || "", articleId || "");
  const catInfo = categoryLabelMap[category || ""];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <i className="ri-emotion-sad-line text-6xl text-foreground-300"></i>
          <h1 className="mt-4 text-2xl font-heading font-bold text-foreground-700">文章不存在</h1>
          <p className="mt-2 text-foreground-500">这篇爆料可能已经被下线了</p>
          <Link to="/content" className="inline-block mt-6 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回今日大瓜
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.categorySlug, article.id);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[55vh] flex items-end pt-20 pb-0">
          <div className="absolute inset-0">
            <img
              src={article.heroImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              title={article.title}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/80"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 pb-12 md:pb-16">
            <div className="max-w-4xl mx-auto">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs text-background-300 mb-4 flex-wrap">
                <Link to="/" className="hover:text-background-100 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/content" className="hover:text-background-100 transition-colors">今日大瓜</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/content/${category}`} className="hover:text-background-100 transition-colors">
                  {catInfo?.name || article.category}
                </Link>
              </div>

              {/* Category tag */}
              <span className={`inline-block px-3 py-1 rounded-full ${catInfo?.bg || "bg-primary-500"} text-background-50 text-xs font-semibold mb-4`}>
                {article.category}
              </span>

              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <span className="text-sm text-background-300">{article.time}</span>
                <div className="w-1 h-1 rounded-full bg-background-400"></div>
                <span className="text-sm text-background-300">{article.views} 次围观</span>
                <div className="w-1 h-1 rounded-full bg-background-400"></div>
                <span className="text-sm text-background-300">51每日大赛编辑部</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full py-10 md:py-16">
          <div className="w-full px-4 md:px-6">
            <article className="max-w-4xl mx-auto">
              {/* Summary */}
              <p className="text-lg md:text-xl text-foreground-800 leading-relaxed font-medium border-l-4 border-primary-400 pl-5 mb-10">
                {article.summary}
              </p>

              {/* Content image */}
              <div className="relative w-full h-[300px] md:h-[450px] rounded-2xl overflow-hidden mb-10">
                <img
                  src={article.contentImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  title={article.title}
                />
              </div>

              {/* Content paragraphs */}
              <div className="space-y-6">
                {article.content.map((paragraph, i) => (
                  <p key={i} className="text-base md:text-lg text-foreground-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Blockquote */}
              <blockquote className="mt-12 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <i className="ri-double-quotes-l text-3xl text-primary-300 mb-3 block"></i>
                <p className="text-lg md:text-xl text-foreground-800 leading-relaxed italic">
                  {article.quote}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-8 h-0.5 bg-primary-400"></div>
                  <span className="text-sm font-semibold text-foreground-600">—— {article.quoteAuthor}</span>
                </div>
              </blockquote>

              {/* Tags */}
              <div className="mt-10 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Divider */}
              <div className="mt-10 pt-10 border-t border-background-200/70"></div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                    相关爆料推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedArticles.slice(0, 2).map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/content/${ra.categorySlug}/detail/${ra.id}`}
                        className="group flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                      >
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={ra.heroImage}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top"
                            title={ra.title}
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                          <span className={`text-xs font-medium ${catInfo?.text || "text-primary-500"}`}>
                            {ra.category}
                          </span>
                          <h4 className="mt-1 text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {ra.title}
                          </h4>
                          <span className="mt-1.5 text-xs text-foreground-500">{ra.time}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Back link */}
              <div className="mt-10 text-center">
                <Link
                  to={`/content/${category}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:border-foreground-900 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{catInfo?.name || "频道"}
                </Link>
              </div>
            </article>
          </div>
        </section>

        {/* Newsletter CTA */}
        <ArticleCTA />
      </main>

      <Footer />
    </div>
  );
}

function ArticleCTA() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [statusMsg, setStatusMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>(".hn-field");
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success"); setStatusMsg("订阅成功！"); setEmail(""); return;
    }
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const fd = new URLSearchParams();
      fd.append("email", email); fd.append("source", "article_detail");
      await fetch("https://readdy.ai/api/form/d90hoh84jh3v1cgksmbg", {
        method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: fd.toString(),
      });
      setStatus("success"); setStatusMsg("订阅成功！更多猛料第一时间送达。"); setEmail("");
    } catch { setStatus("error"); setStatusMsg("网络异常，请稍后重试"); }
  };

  return (
    <section className="relative w-full py-16 md:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-foreground-950"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
          还想看更多猛料？
        </h2>
        <p className="mt-2 text-sm md:text-base text-foreground-400">
          订阅深夜基地，大瓜不眠，推送不停
        </p>
        <form onSubmit={handleSubmit} data-readdy-form className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
          <input type="text" name="website_alt" className="hn-field" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <input type="email" name="email" value={email} onChange={(e) => { setEmail(e.target.value); setStatus("idle"); }} placeholder="输入邮箱" className="flex-1 w-full sm:w-auto px-5 py-3 rounded-full bg-background-50/5 border border-background-300/20 text-background-50 text-sm placeholder-foreground-500 outline-none focus:border-accent-400 transition-colors" required />
          <button type="submit" disabled={status === "loading"} className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50">
            {status === "loading" ? <i className="ri-loader-4-line animate-spin"></i> : "订阅"}
          </button>
        </form>
        {status !== "idle" && <p className={`mt-3 text-sm ${status === "success" ? "text-accent-400" : "text-primary-400"}`}>{statusMsg}</p>}
      </div>
    </section>
  );
}